"use client"

import { ColumnDef } from "@tanstack/react-table"
import { Button } from "@/Components/ui/button"
import { dateformat } from "@/utils/GlobalConstants"
import { format } from "date-fns"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "../ui/dropdown-menu"
import { useContext } from "react"
import { AppContext } from "@/App"
import { Checkbox } from "../ui/checkbox"
import { MoreHorizontal } from "lucide-react"

// This type is used to define the shape of our data.
// You can use a Zod schema here if you want.
export type Report = {
    reportId: string
    name: string
    reportType: "status" | "charter"
    date: Date
}

export function getReportColumns(HandleDeleteClicked:(ids:string[]) => void): ColumnDef<Report>[] {
    const app = useContext(AppContext);
    const columns: ColumnDef<Report>[] = [];

    if(app.authLevel > 3) {
            columns.push(
                {
                    id: "select",
                    header: ({ table }) => (
                        <Checkbox
                            checked={
                                table.getIsAllPageRowsSelected() ||
                                (table.getIsSomePageRowsSelected() && "indeterminate")
                            }
                            onClick={(e) => e.stopPropagation()}
                            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                            aria-label="Select all"
                        />
                    ),
                    cell: ({ row }) => (
                        <Checkbox
                            checked={row.getIsSelected()}
                            onClick={(e) => e.stopPropagation()}
                            onCheckedChange={(value) => row.toggleSelected(!!value)}
                            aria-label="Select row"
                        />
                    ),
                    enableSorting: false,
                    enableHiding: false,
                });
        }
        columns.push(    
    {
        id: "reportId",
        accessorKey: "reportId",
        header: "ID",
        size: 50,
    },
    {
        id: "name",
        accessorKey: "name",
        header: "Report Name",
        meta: {
            filterVariant: 'select',
        },
        cell: ({ row }) => {
            const name = row.getValue("name");
            const type = row.getValue("reportType");
            return <div className="text-center">{name as string} {type === "status" ? "Status" : ""} Report</div>
        },
        size: 120,
    },
    {
        id: "reportType",
        accessorKey: "reportType",
        header: "Report Type",
        meta: {
            filterVariant: 'select',
        },
        cell: ({ row }) => {
            const type = row.getValue("reportType");
            return <div className="text-center">{type as string}</div>
        },
        size: 120,
    },
    {
        id: "date",
        accessorKey: "date",
        header: "Date",
        meta: {
            filterVariant: 'date-range',
        },
        cell: ({ row }) => {
            const date = row.getValue("date");
            return <div className="text-center">{format((date as Date), dateformat)}</div>
        },
        filterFn: (row, columnId, filterValue) => {
            const value = new Date(row.getValue(columnId));
            const from = filterValue.from ? new Date(filterValue.from) : null;
            const to = filterValue.to ? new Date(filterValue.to) : null;
            if (!from || !to) return true;
            return value >= from && value <= to;
        },
        size: 120,
    });

    if(app.authLevel > 3) {
        columns.push(
        {
            id: "actions",
            cell: ({ row }) => {
                const Report = row.original
    
                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem
                                onClick={() => HandleDeleteClicked([Report.reportId])}
                            >
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )
            },
        }
    );
}
return columns;
}

export const columnDisplayNames: [string, string][] = [
    ["reportId", "ID"],
    ["name", "Report Name"],
    ["reportType", "Report Type"],
    ["date", "Date"],
]